import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from 'react-i18next';
import { useCart } from '../context/CartContext';
import { 
    BsSpeedometer2, 
    BsGrid, 
    BsCart, 
    BsClockHistory, 
    BsPerson, 
    BsTags, 
    BsBoxSeam, 
    BsReceipt, 
    BsGear, 
    BsBoxArrowRight,
    BsGraphUp,
    BsPeople,
    BsHeart
} from 'react-icons/bs';
import { FILE_URL } from '../api/axios';

const Sidebar = ({ isOpen, onClose }) => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const user = JSON.parse(localStorage.getItem('user') || sessionStorage.getItem('user'));
    const { settings, darkMode } = useTheme();
    const { cart } = useCart();

    const handleLogout = () => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('token');
        sessionStorage.removeItem('user');
        navigate('/login');
    };

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };

    // Logo URL'sini belirle
    const logoSrc = darkMode && settings.logo_dark 
        ? `${FILE_URL}/${settings.logo_dark}`
        : settings.logo_light 
            ? `${FILE_URL}/${settings.logo_light}`
            : null;

    return (
        <div className={`sidebar-container p-4 d-flex flex-column ${isOpen ? 'show' : ''}`}>
            {/* Mobil İçin Kapatma Butonu */}
            <div className="d-lg-none position-absolute top-0 end-0 p-3">
                <button type="button" className="btn-close" aria-label="Close" onClick={onClose}></button>
            </div>

            <div className="sidebar-heading text-center mb-4 pt-2 px-2">
                {logoSrc ? (
                    <img src={logoSrc} alt="Logo" style={{ maxHeight: '80px', maxWidth: '100%' }} />
                ) : (
                    <h5 className="fw-bold text-primary m-0 text-truncate">{settings.site_title || 'VegaMat'}</h5>
                )}
            </div>
            
            <div className="d-flex flex-column gap-1 flex-grow-1">
                <NavLink to="/dashboard" className={({ isActive }) => `nav-link-custom ${isActive ? 'active' : ''}`}>
                    <BsSpeedometer2 title="Ana Sayfa" />
                    <span>{t('sidebar.dashboard')}</span>
                </NavLink>
                
                <NavLink to="/products" className={({ isActive }) => `nav-link-custom ${isActive ? 'active' : ''}`}>
                    <BsGrid title="Ürün Listesi" />
                    <span>{t('sidebar.products')}</span>
                </NavLink>

                {user?.role !== 'admin' && (
                    <NavLink to="/cart" className={({ isActive }) => `nav-link-custom ${isActive ? 'active' : ''}`}>
                        <BsCart title="Sepetim" />
                        <span>{t('sidebar.cart')}</span>
                        {cart.length > 0 && (
                            <span className="badge bg-danger rounded-pill ms-auto" style={{ fontSize: '0.75rem' }}>{cart.length}</span>
                        )}
                    </NavLink>
                )}

                <NavLink to="/favorites" className={({ isActive }) => `nav-link-custom ${isActive ? 'active' : ''}`}>
                    <BsHeart title="Favorilerim" />
                    <span>{t('sidebar.favorites')}</span>
                </NavLink>

                {user?.role !== 'admin' && (
                    <NavLink to="/my-orders" className={({ isActive }) => `nav-link-custom ${isActive ? 'active' : ''}`}>
                        <BsClockHistory title="Siparişlerim" />
                        <span>{t('sidebar.orders')}</span>
                    </NavLink>
                )}

                {user?.role !== 'admin' && (
                    <NavLink to="/profile" className={({ isActive }) => `nav-link-custom ${isActive ? 'active' : ''}`}>
                        <BsPerson title="Profilim" />
                        <span>{t('sidebar.profile')}</span>
                    </NavLink>
                )}
                
                {/* Sadece Admin Görebilir */}
                {user?.role === 'admin' && (
                    <>
                        <div className="my-2 border-top"></div>
                        <NavLink to="/admin/categories" className={({ isActive }) => `nav-link-custom ${isActive ? 'active' : ''}`}>
                            <BsTags title="Kategoriler" />
                            <span>{t('sidebar.categories')}</span>
                        </NavLink>
                        <NavLink to="/admin/products" className={({ isActive }) => `nav-link-custom ${isActive ? 'active' : ''}`}>
                            <BsBoxSeam title="Ürünler" />
                            <span>{t('sidebar.admin_products')}</span>
                        </NavLink>
                        <NavLink to="/admin/orders" className={({ isActive }) => `nav-link-custom ${isActive ? 'active' : ''}`}>
                            <BsReceipt title="Siparişler" />
                            <span>{t('sidebar.admin_orders')}</span>
                        </NavLink>
                        <NavLink to="/admin/dealers" className={({ isActive }) => `nav-link-custom ${isActive ? 'active' : ''}`}>
                            <BsPeople title="Bayi Yönetimi" />
                            <span>{t('sidebar.dealers')}</span>
                        </NavLink>
                        <NavLink to="/admin/reports" className={({ isActive }) => `nav-link-custom ${isActive ? 'active' : ''}`}>
                            <BsGraphUp title="Raporlar" />
                            <span>{t('sidebar.reports')}</span>
                        </NavLink>
                        <NavLink to="/admin/settings" className={({ isActive }) => `nav-link-custom ${isActive ? 'active' : ''}`}>
                            <BsGear title="Ayarlar" />
                            <span>{t('sidebar.settings')}</span>
                        </NavLink>
                    </>
                )}

                <div className="mt-auto">
                    <div className="d-flex justify-content-center gap-2 mb-2">
                        <button 
                            className={`btn btn-sm ${i18n.language === 'tr' ? 'btn-primary' : 'btn-outline-secondary'}`} 
                            onClick={() => changeLanguage('tr')}>TR</button>
                        <button 
                            className={`btn btn-sm ${i18n.language === 'fr' ? 'btn-primary' : 'btn-outline-secondary'}`} 
                            onClick={() => changeLanguage('fr')}>FR</button>
                    </div>
                    <button onClick={handleLogout} className="nav-link-custom text-danger border-0 bg-transparent w-100 text-start mt-2">
                        <BsBoxArrowRight title="Çıkış Yap" />
                        <span>{t('sidebar.logout')}</span>
                    </button>
                    <div className="text-muted small mt-3 ps-3" style={{ fontSize: '0.7rem', opacity: 0.7, whiteSpace: 'pre-line' }}>
                        {t('sidebar.system_info')}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Sidebar;
